import { useCallback, useId } from 'react';

import {
  Box,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  type Control,
  type FieldErrors,
  type Path,
  useFieldArray,
  useWatch,
} from 'react-hook-form';
import { XCircle } from 'lucide-react';

import HelperTextContainer from '@components/HelperTextContainer';
import FormSelect from '@components/FormFields/FormSelect';
import FilesContainer2 from '@pages/common/Project/Forms/Components/FilesContainer2';
import { useSampleFiles2 } from '@pages/common/Project/Forms/hooks';
import { SampleContainer } from '@pages/common/Project/Forms/StyledComponents';
import {
  type ExperimentStatusSchema,
  type GroupExperimentSchema,
  type SampleFileSchema,
} from '@/zod_schemas';
import SampleIDField from './SampleIDField';

interface ISampleForm2Props {
  index: number;
  experimentIndex: number;
  experimentStatus?: ExperimentStatusSchema;
  errors: FieldErrors<GroupExperimentSchema>;
  control: Control<GroupExperimentSchema>;
}

const SampleForm2 = ({
  index,
  experimentIndex,
  experimentStatus,
  errors,
  control,
}: ISampleForm2Props) => {
  const sampleErrors = errors.samples?.[index];
  const samplePath: Path<GroupExperimentSchema> = `samples.${index}`;
  const fieldIdPrefix = useId();
  const sample = useWatch({ control, name: samplePath });

  const { fields, replace } = useFieldArray({
    control,
    name: `${samplePath}.files`,
  });

  // useCallback is used to prevent infinite loop
  const replaceFiles = useCallback(
    (s: SampleFileSchema[]) => replace(s),
    [replace],
  );

  useSampleFiles2({
    control,
    sampleIndex: index,
    experimentIndex,
    replace: replaceFiles,
    fields,
  });

  const assignmentMessage = (() => {
    if (!sample?.sample_id) {
      return 'The linked sample record is unavailable. Its assignment cannot be changed here.';
    }

    if (sample.active === false) {
      return 'This linked sample is inactive. Its historical experiment assignment remains locked.';
    }

    if (experimentStatus === 'completed') {
      return 'Sample assignment is locked because this experiment is completed.';
    }

    if (experimentStatus === 'in_progress' || experimentStatus === 'waiting') {
      return 'Sample assignment is locked while this experiment is processing.';
    }

    if (experimentStatus === 'failed') {
      return 'Sample assignment is locked to preserve this experiment’s execution history.';
    }

    return 'Sample assignment is read-only in Edit Project. Use a new experiment to change samples safely.';
  })();

  return (
    <SampleContainer
      elevation={0}
      sx={{ mt: 0, p: { xs: 1.75, md: 2 }, borderRadius: 2 }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 2,
          pb: 1.5,
          mb: 2,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Stack spacing={0.25}>
          <Typography variant="subtitle1">Sample {index + 1}</Typography>
          <Typography variant="body2" color="text.secondary">
            Experiment {experimentIndex + 1} sample information and input files
          </Typography>
        </Stack>
        <Tooltip title="Sample removal is unavailable in Edit Project">
          <span>
            <IconButton
              aria-label="remove sample unavailable"
              size="small"
              color="error"
              disabled
            >
              <XCircle size={18} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>
      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <SampleIDField
            value={sample?.sample_id || ''}
            error={Boolean(sampleErrors?.sample_id)}
            locked
            lockedTooltip={assignmentMessage}
            placeholder="Linked sample unavailable"
          />
          {sampleErrors?.sample_id ? (
            <HelperTextContainer>
              {sampleErrors.sample_id.message}
            </HelperTextContainer>
          ) : (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mt: 0.75, px: 0.25 }}
            >
              {assignmentMessage}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel
              id={`${fieldIdPrefix}-sample-type-label`}
              htmlFor={`${fieldIdPrefix}-sample-type`}
            >
              Type
            </InputLabel>
            <FormSelect
              selectProps={{
                labelId: `${fieldIdPrefix}-sample-type-label`,
                id: `${fieldIdPrefix}-sample-type`,
                label: 'Type',
                'aria-describedby': 'sample file type field',
              }}
              controllerProps={{
                control,
                name: `${samplePath}.sample_type`,
              }}
            >
              <MenuItem value="unpaired_fastq">Unpaired FASTQ</MenuItem>
              <MenuItem value="paired_fastq">Paired FASTQ</MenuItem>
              <MenuItem value="bam">BAM</MenuItem>
            </FormSelect>
            <HelperTextContainer>
              {sampleErrors?.sample_type?.message}
            </HelperTextContainer>
          </FormControl>
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth size="small">
            <InputLabel
              id={`${fieldIdPrefix}-sample-gender-label`}
              htmlFor={`${fieldIdPrefix}-sample-gender`}
            >
              Gender
            </InputLabel>
            <FormSelect
              controllerProps={{
                control,
                name: `${samplePath}.gender`,
              }}
              selectProps={{
                labelId: `${fieldIdPrefix}-sample-gender-label`,
                id: `${fieldIdPrefix}-sample-gender`,
                label: 'Gender',
                'aria-describedby': 'sample gender field',
              }}
            >
              <MenuItem value="female">Female</MenuItem>
              <MenuItem value="male">Male</MenuItem>
              <MenuItem value="unknown">Unknown</MenuItem>
            </FormSelect>
            <HelperTextContainer>
              {sampleErrors?.gender && (
                <span>{sampleErrors.gender.message}</span>
              )}
            </HelperTextContainer>
          </FormControl>
        </Grid>
        {fields.length > 0 && (
          <Grid item xs={12}>
            <FilesContainer2
              control={control}
              errors={errors}
              sampleIndex={index}
              fields={fields}
            />
          </Grid>
        )}
      </Grid>
    </SampleContainer>
  );
};

export default SampleForm2;
